import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getProjectList,
  getProjectById,
  createProject,
  updateProject,
  patchProject,
  deleteProject,
} from "../api/projects.api";
import type { ProjectPayload, GetProjectListParams } from "@/interface/profile";
import { projectKeys, candidateProfileKeys, STALE_5_MIN } from "@/lib/queryKey";
import { getNextPage, isPaginated } from "@/utils/helper";

export const useProjectList = (
  profileId?: number | string,
  params?: GetProjectListParams,
) => {
  const hasPagination = isPaginated(params);

  return useInfiniteQuery({
    queryKey: [...projectKeys.list(params), profileId, params],
    queryFn: ({ pageParam }) =>
      getProjectList(
        profileId!,
        hasPagination ? { ...params, page: pageParam } : params,
      ),
    initialPageParam: params?.page ?? 1,
    getNextPageParam: (lastPage, allPages) =>
      hasPagination
        ? getNextPage(lastPage, allPages, params?.page_size)
        : undefined,
    enabled: Boolean(profileId),
  });
};

export const useProjectDetail = (id: string | number, enabled = true) =>
  useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => getProjectById(id),
    enabled: Boolean(id) && enabled,
    ...STALE_5_MIN,
  });

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: candidateProfileKeys.projects(),
      });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: ProjectPayload;
    }) => updateProject(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(id) });
      queryClient.invalidateQueries({
        queryKey: candidateProfileKeys.projects(),
      });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const usePatchProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: Partial<ProjectPayload>;
    }) => patchProject(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(id) });
      queryClient.invalidateQueries({
        queryKey: candidateProfileKeys.projects(),
      });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: candidateProfileKeys.projects(),
      });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};
